import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { resolveAssumptions } from '@dairy/domain';
import {
  deterministicId,
  ProjectionAssumptions,
  type ProjectionAssumptionKey,
  type ProjectionAssumptionSetRecord,
  type ProjectionFarmFigure,
} from '@dairy/shared';
import { useSession, useSettings } from '@/auth/useSession';
import { Badge, Button, Card, Field, Input, PageHeader, Spinner, buttonVariants } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { toast } from '@/components/toast';
import { db } from '@/db/db';
import { save } from '@/db/repo';
import { useFormat } from '@/lib/format';
import { syncNow, useSyncStatus } from '@/sync/client';
import {
  assumptionGroups,
  emptyAssumptions,
  inputValue,
  inputsFrom,
  parseAssumptionInputs,
  scenarioInputsFrom,
} from './assumption-form';
import { useProjectionDefaults } from './useProjectionDefaults';

export function ProjectionAssumptionsPage() {
  const { profile } = useSession();
  const id = deterministicId(`proj-assumptions:${profile!.tenantId}`);
  const loaded = useLiveQuery(async () => ({ record: await db.projectionAssumptionSets.get(id) }), [id]);
  if (!loaded) return <Spinner />;
  return <AssumptionsForm key={id} id={id} existing={loaded.record} />;
}

function AssumptionsForm({ id, existing }: { id: string; existing?: ProjectionAssumptionSetRecord }) {
  const { t } = useTranslation();
  const f = useFormat();
  const settings = useSettings();
  const defaults = useProjectionDefaults();
  const sync = useSyncStatus();
  const [inputs, setInputs] = useState(() => inputsFrom(existing?.data ?? emptyAssumptions()));
  const [advanced, setAdvanced] = useState(() => scenarioInputsFrom(existing?.data ?? emptyAssumptions()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const savingRef = useRef(false);
  const parsed = parseAssumptionInputs(inputs, advanced);
  const resolution = resolveAssumptions(parsed.data, defaults.cached?.data.values ?? {}, settings);
  const invalid = Object.keys(parsed.errors).length > 0;
  useEffect(() => {
    if (sync.lastSyncAt && defaults.online) void defaults.refresh();
  }, [sync.lastSyncAt, defaults.online, defaults.refresh]);
  const format = (key: ProjectionAssumptionKey, value: number | null) =>
    value === null
      ? '—'
      : key.endsWith('Santim')
        ? f.birr(value)
        : key.endsWith('Ml') || key === 'calfMilkMlPerDay'
          ? f.liters(value)
          : inputValue(key, value);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (invalid || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await save('projectionAssumptionSet', { ...(existing ?? {}), id, data: parsed.data });
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
      if (navigator.onLine)
        void syncNow()
          .then((outcome) => (outcome === 'ok' ? defaults.refresh() : undefined))
          .catch(() => undefined);
    } catch (e) {
      setError(e);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const figureDetail = (figure: ProjectionFarmFigure | undefined) =>
    figure ? (
      <>
        <p>{t(`projections.sources.${figure.source}`)}</p>
        {figure.from && figure.to ? (
          <p>
            {f.short(figure.from)} – {f.short(figure.to)}
          </p>
        ) : null}
        <p>
          {t(`projections.samples.${figure.sampleUnit}`, {
            count: figure.sample,
            defaultValue: t('projections.sample', { count: figure.sample }),
          })}
        </p>
        {figure.reason ? <p>{t(`projections.reasons.${figure.reason}`)}</p> : null}
      </>
    ) : (
      <p>{t('projections.sources.NONE')}</p>
    );
  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <PageHeader
        title={t('projections.assumptionsTitle')}
        actions={
          <Link to="/projections/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            {t('projectionForm.title')}
          </Link>
        }
      />
      <p className="max-w-3xl text-stone-600">{t('projections.overrideHint')}</p>
      <Link to="/projections" className="inline-block text-sm font-medium text-brand-800 underline">{t('projectionSaved.title')}</Link>
      <Card className="space-y-3 border-brand-100 bg-brand-50/40">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge tone={resolution.missing.length || invalid ? 'amber' : 'green'}>
            {resolution.missing.length
              ? t('projections.missingCount', { count: resolution.missing.length })
              : invalid
                ? t('projections.correctErrors')
                : t('projections.ready')}
          </Badge>
          <Button
            variant="secondary"
            disabled={!defaults.online || defaults.loading}
            onClick={() => void defaults.refresh()}
          >
            {defaults.loading ? t('app.loading') : t('projections.refresh')}
          </Button>
        </div>
        <p className="text-sm font-medium">{t('projections.feedTip')}</p>
        {defaults.cached ? (
          <p className="text-sm text-stone-600">
            {t('projections.refreshed', {
              date: f.date(defaults.cached.data.asOf),
              time: f.time(defaults.cached.fetchedAt),
            })}
          </p>
        ) : (
          <p className="text-sm text-stone-600">{t('projections.noFarmFigures')}</p>
        )}
        {defaults.cached && (!defaults.online || defaults.error) ? (
          <p className="text-sm text-amber-900">{t('projections.cachedHint')}</p>
        ) : null}
        {defaults.error ? (
          <p role="alert" className="text-sm text-red-700">
            {errorMessage(t, defaults.error)}
          </p>
        ) : null}
      </Card>
      {assumptionGroups.map((group) => (
        <Card key={group.name} className="overflow-hidden p-0">
          <h2 className="border-b border-stone-200 bg-stone-50 px-4 py-3 font-semibold">
            {t(`projections.groups.${group.name}`)}
          </h2>
          <div className="divide-y divide-stone-200">
            {group.keys.map((key) => {
              const used = resolution.values[key],
                figure = defaults.cached?.data.values[key];
              const missing = resolution.missing.includes(key),
                fieldId = `assumption-${key}`;
              const shape = ProjectionAssumptions.omit({ scenarios: true }).shape[key].unwrap();
              return (
                <div
                  key={key}
                  className={`grid min-w-0 grid-cols-2 gap-4 p-4 md:grid-cols-[1.3fr_1fr_1fr_1fr] ${missing ? 'bg-amber-50/60' : ''}`}
                >
                  <div className="col-span-2 md:col-span-1">
                    <label htmlFor={fieldId} className="font-semibold">
                      {t(`projections.fields.${key}`)}
                    </label>
                    {missing ? (
                      <p id={`${fieldId}-missing`} className="mt-1 text-sm text-amber-900">
                        {t('projections.setFirst')}
                      </p>
                    ) : null}
                  </div>
                  <Field
                    className="col-span-2 md:col-span-1"
                    label={t('projections.yourValue')}
                    htmlFor={fieldId}
                    hint={t(missing ? 'projections.requiredHint' : 'projections.blankHint')}
                  >
                    <Input
                      id={fieldId}
                      value={inputs[key]}
                      onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
                      inputMode={key === 'calfMilkDays' ? 'numeric' : 'decimal'}
                      autoComplete="off"
                      placeholder="—"
                      aria-invalid={!!parsed.errors[key]}
                      aria-describedby={
                        parsed.errors[key] ? `${fieldId}-error` : missing ? `${fieldId}-missing` : undefined
                      }
                    />
                    {parsed.errors[key] ? (
                      <p id={`${fieldId}-error`} role="alert" className="text-sm text-red-700">
                        {t('projections.invalidRange', {
                          min: format(key, shape.minValue),
                          max: format(key, shape.maxValue),
                        })}
                      </p>
                    ) : null}
                  </Field>
                  <div className="min-w-0">
                    <p className="mb-1 text-sm font-medium text-stone-600">{t('projections.farmFigure')}</p>
                    <p className="break-words font-medium tabular-nums">
                      {format(key, figure?.value ?? null)}
                    </p>
                    <div className="mt-1 space-y-1 text-xs text-stone-500">{figureDetail(figure)}</div>
                  </div>
                  <div className="min-w-0">
                    <p className="mb-1 text-sm font-medium text-stone-600">{t('projections.inUse')}</p>
                    <p
                      data-testid={`in-use-${key}`}
                      className="break-words text-lg font-bold tabular-nums text-brand-900"
                    >
                      {parsed.errors[key] ? '—' : format(key, used.value)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500">
                      {t(`projections.sources.${parsed.errors[key] ? 'NONE' : used.source}`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}
      <Card>
        <details open={Object.keys(parsed.errors).some((key) => key.includes('.')) || undefined}>
          <summary className="min-h-12 cursor-pointer py-3 font-semibold">
            {t('projections.advanced')}
          </summary>
          <p className="mb-4 text-sm text-stone-600">{t('projections.advancedHint')}</p>
          <div className="grid gap-5 sm:grid-cols-2">
            {(['low', 'high'] as const).map((kind) => (
              <fieldset key={kind} className="space-y-3">
                <legend className="mb-2 font-semibold">{t(`projections.scenarios.${kind}`)}</legend>
                {(['yieldBp', 'priceBp', 'feedBp', 'spcAddX10'] as const).map((key) => {
                  const fieldId = `scenario-${kind}-${key}`;
                  return (
                    <Field
                      key={key}
                      htmlFor={fieldId}
                      label={t(`projections.multipliers.${key}`)}
                      error={
                        parsed.errors[`${kind}.${key}`]
                          ? t(
                              key === 'spcAddX10'
                                ? 'projections.invalidAdjustment'
                                : 'projections.invalidPercent',
                            )
                          : undefined
                      }
                    >
                      <Input
                        id={fieldId}
                        value={advanced[kind][key]}
                        inputMode={key === 'spcAddX10' ? 'text' : 'decimal'}
                        aria-invalid={!!parsed.errors[`${kind}.${key}`]}
                        onChange={(e) =>
                          setAdvanced({ ...advanced, [kind]: { ...advanced[kind], [key]: e.target.value } })
                        }
                      />
                    </Field>
                  );
                })}
              </fieldset>
            ))}
          </div>
        </details>
      </Card>
      <Card className="space-y-3">
        {error ? (
          <p role="alert" className="text-red-700">
            {errorMessage(t, error)}
          </p>
        ) : null}
        <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={saving || invalid}>
          {saving ? t('app.loading') : t('projections.save')}
        </Button>
      </Card>
    </form>
  );
}
